import { useUser } from '@folklore/auth';
import classNames from 'classnames';
import { camelCase } from 'lodash';
import uniq from 'lodash/uniq';
import React, { useContext, useEffect, useMemo, useState } from 'react';

interface ThemeContextType extends ContestTheme {
    isVariant: (variant: string) => boolean;
}

const ThemeContext = React.createContext<ThemeContextType>({
    name: null,
    variant: null,
    isVariant: () => false,
});

export function useTheme(): ThemeContextType {
    const theme = useContext(ThemeContext);
    return theme;
}

export function useThemeClassName(styles, addGlobal: boolean = false): string {
    const theme = useTheme();
    const { name, variant = null } = theme;
    return classNames([
        {
            [camelCase(`theme-${name}`)]: addGlobal,
            ...(variant || []).reduce(
                (map, it) => ({
                    ...map,
                    [camelCase(`variant-${it}`)]: addGlobal,
                }),
                {},
            ),
        },
        styles[`${camelCase(name)}Theme`],
        ...(variant || []).map((it) => styles[`${it}Variant`]),
    ]);
}

interface ThemeProviderProps {
    children: React.ReactNode;
    theme: Theme;
    user?: Participant;
    test?: boolean;
}

const testVariants = ['generic', 'mon', 'tor', 'ott', 'edm', 'van'];

export function ThemeProvider({ children, theme, test = false }: ThemeProviderProps) {
    const previousTheme = useTheme();
    const { groups = null } = useUser() || {};
    const [temporaryVariant, setTemporaryVariant] = useState(null);

    const userVariants = useMemo(
        () =>
            (groups || [])
                .filter((it) => it.match(/^skin[_-]/) !== null)
                .map((it) => it.replace(/^skin[_-]/, '')),
        [groups],
    );

    useEffect(() => {
        let id = null;
        if (test) {
            id = setTimeout(() => {
                const idx = testVariants.indexOf(temporaryVariant) || 0;
                const nextIndex = idx + 1 >= testVariants.length ? 0 : idx + 1;
                setTemporaryVariant(testVariants[nextIndex]);
            }, 1000);
        }
        return () => {
            if (id !== null) {
                clearTimeout(id);
            }
        };
    }, [test, temporaryVariant, setTemporaryVariant]);

    const newTheme = useMemo(() => {
        const { variant: previousVariant = null } = previousTheme || {};
        const { variant: themeVariant = null } = theme || {};
        const newVariant = uniq(
            [...(previousVariant || []), themeVariant, ...(userVariants || [])].filter(
                (it) => it !== null,
            ),
        );
        const defaultVariants = newVariant.length > 0 ? newVariant : null;
        return {
            ...previousTheme,
            ...theme,
            variant: temporaryVariant !== null ? [temporaryVariant] : defaultVariants,
            isVariant: (it) => newVariant !== null && newVariant.indexOf(it) !== -1,
        };
    }, [previousTheme, theme, userVariants, temporaryVariant]);

    return <ThemeContext.Provider value={newTheme}>{children}</ThemeContext.Provider>;
}

export default ThemeContext;
